/**
 * TenantDataStates (CHAP-33) — shared loading skeleton + error/retry states
 * for every page/component reading `useTenantData`.
 *
 * Replaces the per-page "Loading…" text + plain error text that was
 * duplicated across the tenant-data-driven pages (Overview, KPIs,
 * EntityStructure, Fundraising, Financial, Voting, Competitive, Parking,
 * RebuildVsRenovate, OKRs, ExecutiveSummary).
 */

export function TenantLoading() {
  return (
    <div className="space-y-4 sm:space-y-5 min-w-0" role="status" aria-label="Loading tenant data">
      {/* Header block */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="h-6 w-1/3 bg-gray-200 rounded animate-pulse" />
        <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse mt-3" />
      </div>

      {/* Tile row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>

      {/* List block */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function TenantError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center" role="alert">
      <p className="text-sm text-red-700 font-medium">Could not load tenant data</p>
      <p className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 px-4 py-2 rounded bg-navy text-white text-sm font-medium hover:opacity-90"
      >
        Try again
      </button>
    </div>
  );
}
